import axios from 'axios';
import { toast } from 'react-toastify';
import { decodeToken } from '../utils/jwtUtils';
import { socket } from '../config/Socket';
import config from '../config';

export const apiInstance = axios.create({
  baseURL: config.BASE_URL
});

apiInstance.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem('callex-token'));
    config.headers.Authorization = token ? `Bearer ${token}` : undefined;
    return config;
  },
  (error) => Promise.reject(error)
);

// The API replies with one of these (401/403) once the session is no longer valid.
// Add any further auth-failure wording the backend uses here.
const SESSION_EXPIRED_MESSAGES = [
  'Token expired.',
  'Access denied. No token provided.',
  'Invalid token.'
];

let isSessionEnding = false;

// Tears the session down exactly like the logout button does.
// Every polling widget on a screen fails in the same instant, so this has to be
// idempotent - previously each one stacked its own toast and re-ran the teardown.
const endSession = () => {
  if (isSessionEnding) return;
  isSessionEnding = true;

  try {
    const token = JSON.parse(localStorage.getItem('callex-token'));
    // decodeToken throws on a missing token, and by the time the second request
    // fails the token may already be gone.
    const user = token ? decodeToken(token) : null;
    if (user?.type === 'User') {
      socket.emit('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
    }
  } catch (e) {
    console.log('could not notify logout', e);
  }

  // Keep SIP from firing reconnect attempts while we tear down.
  window.__SIP_MANUAL_LOGOUT__ = true;

  // localStorage also holds the redux-persist root, so this drops the persisted
  // store with it. The reload then boots with no token and the route guards send
  // the user to the login page matching their type (/login, /master_login,
  // /auditor_login) — a hardcoded redirect would get master admin and auditor wrong.
  localStorage.clear();
  localStorage.removeItem('timerDuration');
  localStorage.removeItem('timerStartTime');
  window.location.reload();
};

// Add a response interceptor
apiInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.log('error', error);
    if (!error.response) {
      return Promise.reject(error);
    }

    // `data` is a Blob on failed file downloads, so read it defensively.
    const data = error.response.data || {};
    const status = error.response.status;
    const message = typeof data.message === 'string' ? data.message : '';

    // Session is gone — log out instead of showing a toast. Matched on the message
    // rather than the status alone so a genuine permission-denied 403 still just
    // warns the user instead of throwing them out.
    if ((status === 401 || status === 403) && SESSION_EXPIRED_MESSAGES.includes(message)) {
      endSession();
      return Promise.reject(error);
    }

    if (typeof data.message === 'string' || typeof data.error === 'string') {
      if (data.message === 'Internal Server Error') {
        toast.error(data.error);
      } else {
        toast.error(data.message || data.error);
      }
    } else {
      for (let i = 0; i < data?.message?.length; i++) {
        toast.error(data.message[i]);
      }
    }
    return Promise.reject(error);
  }
);
